"use client";

import { FormEvent, useMemo, useState } from "react";

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
};

const starterQuestions = [
  "What is Khalid currently working on?",
  "How did his career evolve from engineering to AI leadership?",
  "What are his strongest capabilities?",
];

export default function DigitalTwinChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: "assistant",
      content:
        "I am Khalid's Digital Twin. Ask me anything about his career journey, AI leadership, and transformation work.",
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSend = useMemo(
    () => input.trim().length > 0 && !isLoading,
    [input, isLoading],
  );

  async function sendMessage(message: string) {
    const userContent = message.trim();
    if (!userContent || isLoading) return;

    setError(null);
    const nextMessages = [...messages, { role: "user" as const, content: userContent }];
    setMessages(nextMessages);
    setInput("");
    setIsLoading(true);

    try {
      const response = await fetch("/api/digital-twin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: nextMessages }),
      });

      const data = (await response.json()) as { reply?: string; error?: string };
      if (!response.ok || !data.reply) {
        throw new Error(data.error || "Chat request failed.");
      }

      setMessages((prev) => [...prev, { role: "assistant", content: data.reply! }]);
    } catch (err) {
      const messageText =
        err instanceof Error ? err.message : "Unexpected error while contacting Digital Twin.";
      setError(messageText);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await sendMessage(input);
  }

  return (
    <section className="glass-panel rounded-3xl p-8 md:p-10">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-semibold text-white md:text-3xl">
          Ask the Digital Twin
        </h2>
        <span className="rounded-full border border-cyan-300/30 bg-cyan-400/10 px-4 py-1.5 text-xs uppercase tracking-[0.18em] text-cyan-200">
          OpenRouter AI
        </span>
      </div>

      <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
        <div className="max-h-[28rem] space-y-3 overflow-y-auto pr-1">
          {messages.map((message, index) => (
            <article
              key={`${message.role}-${index}`}
              className={`max-w-[92%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                message.role === "assistant"
                  ? "bg-white/[0.06] text-zinc-100"
                  : "ml-auto border border-cyan-300/30 bg-cyan-400/15 text-cyan-50"
              }`}
            >
              {message.content}
            </article>
          ))}
          {isLoading && (
            <article className="max-w-[92%] rounded-2xl bg-white/[0.06] px-4 py-3 text-sm text-zinc-200">
              Thinking...
            </article>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {starterQuestions.map((question) => (
            <button
              key={question}
              type="button"
              onClick={() => sendMessage(question)}
              disabled={isLoading}
              className="rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-xs text-zinc-200 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {question}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="mt-4 flex gap-2">
          <input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="Ask about leadership, projects, milestones..."
            className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-zinc-400 focus:border-cyan-300/50 focus:outline-none"
          />
          <button
            type="submit"
            disabled={!canSend}
            className="rounded-xl bg-white px-5 py-3 text-sm font-semibold text-black transition hover:bg-zinc-200 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Send
          </button>
        </form>

        {error && <p className="mt-3 text-sm text-rose-300">{error}</p>}
      </div>
    </section>
  );
}
